import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, MapPin, Shield, AlertTriangle, Users, Phone, Eye, HelpCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Hint = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: 'rgba(235, 110, 31, 0.04)', border: '1px solid rgba(235, 110, 31, 0.12)', padding: '0.65rem 0.85rem', marginBottom: '1rem', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
    <HelpCircle size={15} style={{ color: 'var(--astros-orange)', flexShrink: 0, marginTop: 2 }} />
    <span>{text}</span>
  </div>
);

const SimpleBar = ({ label, value, max, color = 'var(--astros-orange)' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: '0.65rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{value}</span>
      </div>
      <div style={{ height: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 5, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
};

const AnalyticsView = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)', fontSize: '1.3rem' }}>Loading your analytics report...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>Failed to load analytics.</div>;

  const chargeMax = Math.max(...Object.values(data.charge_breakdown || {}), 1);
  const areaMax = Math.max(...Object.values(data.area_breakdown || {}), 1);
  const bondMax = Math.max(...Object.values(data.bond_ranges || {}), 1);
  const relMax = Math.max(...Object.values(data.relation_breakdown || {}), 1);

  const weekTrend = data.this_week - data.last_week;
  const dailyMax = Math.max(...(data.daily_stats || []).map(d => d.submissions), 1);

  return (
    <div data-testid="analytics-view">
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="dash-content__title" data-testid="analytics-title">
          <BarChart3 size={28} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 10, color: 'var(--astros-orange)' }} />
          YOUR BUSINESS REPORT
        </h1>
        <p className="dash-content__subtitle">
          Everything you need to know about how your business is doing, all in one place.
        </p>
      </div>

      <Hint text="This report shows you how many people are reaching out, what they're getting arrested for, where they're coming from, and how your website is performing. Use it to understand your business at a glance." />

      {/* ── Top-Level Numbers ─────────────────────────────── */}
      <div className="dash-stats" data-testid="analytics-top-stats" style={{ marginBottom: '2rem' }}>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Shield size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> TOTAL CLIENTS</div>
          <div className="dash-stat-card__value">{data.total_submissions}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>People who filled out forms</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Eye size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> WEBSITE VISITORS</div>
          <div className="dash-stat-card__value">{data.page_views}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>How many people visited your site</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Phone size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> PHONE CALLS</div>
          <div className="dash-stat-card__value">{data.call_clicks}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>People who tapped "Call Now"</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><MapPin size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> DIRECTIONS</div>
          <div className="dash-stat-card__value">{data.direction_clicks}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>People who looked up your address</div>
        </div>
      </div>

      {/* ── Week Comparison ──────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'white', letterSpacing: '0.04em' }}>THIS WEEK</h3>
            <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', background: weekTrend >= 0 ? 'rgba(46,229,157,0.1)' : 'rgba(191,10,48,0.1)', border: `1px solid ${weekTrend >= 0 ? 'rgba(46,229,157,0.3)' : 'rgba(191,10,48,0.3)'}`, color: weekTrend >= 0 ? 'var(--success)' : 'var(--texas-red)', fontWeight: 700 }}>
              {weekTrend >= 0 ? <TrendingUp size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} /> : <TrendingDown size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />}
              {weekTrend >= 0 ? '+' : ''}{weekTrend} vs last week
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--astros-orange)', lineHeight: 1 }}>{data.this_week}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>new submissions this week</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.75rem' }}>LAST WEEK</h3>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--text-secondary)', lineHeight: 1 }}>{data.last_week}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>submissions the week before</div>
        </div>
      </div>

      {/* ── Daily Chart ──────────────────────────────────── */}
      <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)', marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>DAILY ACTIVITY — LAST 30 DAYS</h3>
        <Hint text="Each bar below represents one day. Taller bars mean more people submitted forms that day. This helps you see which days of the week are busiest." />
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100, paddingTop: '0.5rem' }}>
          {(data.daily_stats || []).map((d, i) => (
            <div key={i} title={`${d.label}: ${d.submissions}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                width: '100%', minHeight: 3,
                height: `${Math.max(3, (d.submissions / dailyMax) * 80)}px`,
                background: d.submissions > 0 ? 'var(--astros-orange)' : 'rgba(255,255,255,0.05)',
                transition: 'height 0.4s ease',
              }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem' }}>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>30 days ago</span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Today</span>
        </div>
      </div>

      {/* ── Two-Column Breakdowns ─────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>

        {/* Charge Breakdown */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
            <AlertTriangle size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: 'var(--texas-red)' }} />
            TYPES OF CHARGES
          </h3>
          <Hint text="This shows what people are most commonly getting arrested for. DWI, assault, and drug offenses are typical for Houston." />
          {Object.keys(data.charge_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No charge data yet. This will populate as forms come in.</p>
          ) : Object.entries(data.charge_breakdown).map(([k, v]) => (
            <SimpleBar key={k} label={k} value={v} max={chargeMax} color="var(--texas-red)" />
          ))}
        </div>

        {/* Bond Ranges */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
            BOND AMOUNT RANGES
          </h3>
          <Hint text="This shows you the dollar ranges of the bonds people are applying for. Higher bonds mean bigger cases and bigger premiums for you." />
          {Object.entries(data.bond_ranges || {}).filter(([,v]) => v > 0).map(([k, v]) => (
            <SimpleBar key={k} label={k} value={v} max={bondMax} color="var(--astros-orange)" />
          ))}
          {Object.values(data.bond_ranges || {}).every(v => v === 0) && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No bond data yet.</p>
          )}
        </div>

        {/* Service Area */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
            <MapPin size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: 'var(--astros-orange)' }} />
            WHERE CLIENTS COME FROM
          </h3>
          <Hint text="These are the cities and neighborhoods your clients live in. This tells you which areas need you the most." />
          {Object.keys(data.area_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No location data yet. This populates from form addresses.</p>
          ) : Object.entries(data.area_breakdown).map(([k, v]) => (
            <SimpleBar key={k} label={k} value={v} max={areaMax} />
          ))}
        </div>

        {/* Relationship */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
            <Users size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
            WHO IS CALLING FOR HELP
          </h3>
          <Hint text="This shows you the relationship between the person filling out the form and the person in jail. Most commonly it's a spouse, parent, or friend." />
          {Object.keys(data.relation_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No relationship data yet.</p>
          ) : Object.entries(data.relation_breakdown).map(([k, v]) => (
            <SimpleBar key={k} label={k} value={v} max={relMax} color="#6366f1" />
          ))}
        </div>
      </div>

      {/* ── Demographics ────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>AGE GROUPS</h3>
          <Hint text="Age range of the people filling out forms (the indemnitor, not the defendant)." />
          {Object.entries(data.age_groups || {}).filter(([,v]) => v > 0).map(([k, v]) => (
            <SimpleBar key={k} label={k} value={v} max={Math.max(...Object.values(data.age_groups || {}), 1)} color="#818cf8" />
          ))}
        </div>
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>GENDER</h3>
          <Hint text="Gender of the people filling out forms." />
          {Object.entries(data.sex_breakdown || {}).map(([k, v]) => (
            <SimpleBar key={k} label={k === 'M' ? 'Male' : k === 'F' ? 'Female' : k} value={v} max={Math.max(...Object.values(data.sex_breakdown || {}), 1)} color="#14b8a6" />
          ))}
          {Object.keys(data.sex_breakdown || {}).length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No gender data yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;
